import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { orgID, post, useSession } from '@yingbaba/api';
import { Button, ErrorNotice } from '@yingbaba/ui';
import type { SupportCaseView } from '@yingbaba/types';

export function OrderSupport(props: OrderSupportProps) {
  const session = useSession();
  return (
    <OrderSupportSession
      key={`${session.data?.account.id}:${orgID()}:${props.kind}:${props.orderID}`}
      {...props}
    />
  );
}
type OrderSupportProps = {
  kind: 'digital' | 'member';
  orderID: string;
  disabled?: boolean;
};
function OrderSupportSession({ kind, orderID, disabled }: OrderSupportProps) {
  const session = useSession();
  const scope = `${session.data?.account.id}:${orgID()}:${kind}:${orderID}`;
  const current = useRef(scope);
  current.current = scope;
  const alive = useRef(true);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  async function contact() {
    if (disabled || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      const value = await post<SupportCaseView>('/support-cases', {
        order_kind: kind,
        order_id: orderID,
      });
      if (!alive.current || current.current !== scope) return;
      void qc.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).startsWith('/conversations'),
      });
      const params = new URLSearchParams({ conversation: value.conversation.id });
      if (value.organization_id && value.organization_id !== orgID())
        params.set('organization_id', value.organization_id);
      navigate(`/messages?${params}`);
    } catch (error) {
      if (alive.current && current.current === scope) setError(error);
    } finally {
      pending.current = false;
      if (alive.current && current.current === scope) setBusy(false);
    }
  }
  return (
    <div className="order-support-action">
      <Button
        type="button"
        variant="secondary"
        loading={busy}
        disabled={disabled || busy}
        onClick={() => void contact()}
      >
        订单咨询
      </Button>
      <ErrorNotice error={error} />
    </div>
  );
}
